import { useMemo } from "react";
import { Button } from "@/components/ui/Button";
import type { ClubPaymentSettingsFull } from "@/api/clubsApi";

interface ClubPricingSelectorProps {
  paymentSettings: ClubPaymentSettingsFull;
  selectedInterval: "month" | "year";
  onSelectInterval: (interval: "month" | "year") => void;
  onContinue: () => void;
  loading?: boolean;
  trialDays?: number;
  buttonLabel?: string;
  buttonClassName?: string;
}

export function ClubPricingSelector({
  paymentSettings,
  selectedInterval,
  onSelectInterval,
  onContinue,
  loading = false,
  trialDays = 0,
  buttonLabel,
  buttonClassName,
}: ClubPricingSelectorProps) {
  const monthlyPrice = paymentSettings?.membership_price_monthly || 0;
  const yearlyPrice = paymentSettings?.membership_price_yearly || 0;

  const availableIntervals = useMemo(() => {
    const intervals: ("month" | "year")[] = [];
    if (monthlyPrice > 0) intervals.push("month");
    if (yearlyPrice > 0) intervals.push("year");
    return intervals;
  }, [monthlyPrice, yearlyPrice]);

  const savingsPercent = useMemo(() => {
    if (monthlyPrice > 0 && yearlyPrice > 0) {
      const monthlyAnnualized = monthlyPrice * 12;
      const savings = ((monthlyAnnualized - yearlyPrice) / monthlyAnnualized) * 100;
      return Math.round(savings);
    }
    return 0;
  }, [monthlyPrice, yearlyPrice]);

  if (availableIntervals.length === 0) {
    return null;
  }

  return (
    <>
      {/* Trial info */}
      {trialDays > 0 && (
        <p className="text-body-2 text-text-secondary mb-6 text-center">
          Start your {trialDays}-day free trial. Choose a plan — cancel anytime.
        </p>
      )}

      {/* Price Options */}
      <div className="space-y-4 mb-12">
        {availableIntervals.includes("month") && (
          <button
            onClick={() => onSelectInterval("month")}
            className={`w-full p-5 rounded-2xl text-left border-2 transition-colors ${
              selectedInterval === "month"
                ? "border-text-primary"
                : "border-border-tertiary"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-h6 text-text-primary mb-1">Monthly</p>
                <p className="text-body-1 text-text-primary mb-3">
                  ${(monthlyPrice / 100).toFixed(2)}/mo
                </p>
                <p className="text-body-3 text-text-tertiary">
                  Billed at ${(monthlyPrice / 100).toFixed(2)} per month
                </p>
              </div>
              {/* Radio button */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedInterval === "month" ? "border-text-primary" : "border-border-tertiary"
              }`}>
                {selectedInterval === "month" && (
                  <div className="w-2.5 h-2.5 rounded-full bg-text-primary" />
                )}
              </div>
            </div>
          </button>
        )}

        {availableIntervals.includes("year") && (
          <button
            onClick={() => onSelectInterval("year")}
            className={`w-full p-5 rounded-2xl text-left border-2 transition-colors ${
              selectedInterval === "year"
                ? "border-text-primary"
                : "border-border-tertiary"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-h6 text-text-primary mb-1">Yearly</p>
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-body-1 text-text-primary">
                    ${(yearlyPrice / 100 / 12).toFixed(2)}/mo
                  </p>
                  {savingsPercent > 0 && (
                    <span className="bg-accent-positive text-background text-body-3-medium px-3 py-1 rounded-xl">
                      Save {savingsPercent}%
                    </span>
                  )}
                </div>
                <p className="text-body-3 text-text-tertiary">
                  Billed at ${(yearlyPrice / 100).toFixed(2)} per year
                </p>
              </div>
              {/* Radio button */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedInterval === "year" ? "border-text-primary" : "border-border-tertiary"
              }`}>
                {selectedInterval === "year" && (
                  <div className="w-2.5 h-2.5 rounded-full bg-text-primary" />
                )}
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Continue Button */}
      <Button
        onClick={onContinue}
        disabled={loading || availableIntervals.length === 0}
        className={`w-full h-12 rounded-full ${buttonClassName || ""}`}
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
        ) : (
          (buttonLabel || "Continue")
        )}
      </Button>
    </>
  );
}
